// Third party
import React, { memo } from 'react';
import classNames from 'classnames';

// Namespaces
import * as ProposalNS from '../../Proposal';
import * as ProposalElementsNS from '../../ProposalElements';

// Redux
import { ProposalTemplateOptions } from '@/redux/modules/types/proposal-v2';

// Helpers
import { registerDAOs } from '../../DAOs/DAORegistry';
import { getMaxNumberOfItems, validateDisableRules } from '../../ProposalHelpers';
import { isEqual } from 'lodash';
import { DAOManager } from '../../DAOs/DAOManager';

// Utils
import { buildProposalSectionElements } from '../../ProposalElementBuilder';
import { DropItemPosition } from '@/utils/drag-and-drop';
import { deepClone } from '@/utils/objects';
import { ProposalTemplateUtils } from '../../ProposalTemplateUtils';
import { comparisonIsReadOnly } from '@/utils/comparison';
import { filterSectionsByType, sortSectionsBySectionKey } from '../../utils/proposal';
import { remapAccountSetIndices } from '../../utils/account-set-helpers';
import {
  calculateAssetAllocationAdditionalPages,
  calculateAssetAllocationDonutAdditionalPages,
  hasAssetAllocationData,
  hasAssetAllocationDonutData,
  getAssetAllocationRowCount,
  getAssetAllocationDonutData,
} from '@/utils/portfolio/allocation';

// Components
import ProposalSidebar from '../proposal-sidebar/ProposalSidebar';
import ProposalPreviewComponent from './ProposalPreviewComponent';
import ProposalStyleLoader from '@/views/components/ProposalStyleLoader';
import {
  preprocessCorrelationMatrices,
  preprocessBMOGif,
  preprocessIncomeStream,
  preprocessRiskProfiles,
  preprocessSuitabilityDetermination,
  preprocessInvestorQuestionnaire,
  preprocessInstrumentCustomDisclaimers,
  preprocessRebalanceAnalysis,
  preprocessBenchmarksBreakdownDisclaimers,
} from '../../customSections/ProposalCustomSections';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import ProposalToolbox from '../proposal-toolbox/ProposalToolbox';
import BottomToolbox from '@/modules/proposal/components/proposal-toolbox/BottomToolbox';

import Dictionary from '@/types/dictionary';
import Nullable from '@/types/nullable';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import ProposalTextAreaData from '@/types/proposal-text-area-data';
import User from '@/types/user';
import Locale from '@/types/locale';
import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import CorrelationMetric from '@/types/correlation-metric';
import MockableUserInfos from '@/types/mockable-user-infos';
import RiskProfileItem from '@/types/risk-profile-item';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import ComparisonToolbarOptions from '@/types/comparison-toolbar-options';
import AdvisorContext from '@/types/advisor-context';
import QuestionnaireAnswers from '@/types/questionnaire-answers';
import LocalizedString from '@/types/localized-string';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import { getCommonInception } from '@/utils/common-inception';
import { ComparisonItemsFilter } from '@/redux/modules/types/comparison';
import ProposalOmniRiskProfilesQuestionnaire from '@/types/risk-profiles/omni/proposal-omni-risk-profiles-questionnaire';
import HistoricalScenario from '@/types/historical-scenario';
import CalculatedHistoricalScenario from '@/types/calculated-historical-scenario';
import ProposalPaperFormat from '@/types/proposal-paper-format';
import ProposalOrientation from '@/types/proposal-orientation';
import { PROPOSAL_BANNER_STYLES } from '../../utils/constants';
import { ProposalActionsProvider, ProposalActions } from './ProposalActionsContext';

type Props = {
  user: User;
  locale: Locale;
  comparison: Comparison;
  template: ProposalNS.ProposalTemplateV2;
  templates: ProposalNS.BaseProposalTemplate[];
  proposalItems: ComparisonItem[];
  proposalItemsCorrelation?: CorrelationMetric;
  proposalGifItems: BMOGIFItem[];
  proposalUser: MockableUserInfos;
  selectedSidebarTab?: string;
  calculateHistoricalScenarios: (historicalScenarios: HistoricalScenario[], togetherMode: boolean) => Promise<CalculatedHistoricalScenario[]>;
  onToggleHiddenLayout: (hiddenLayout: string, selected: boolean) => void;
  onToggleSection: (section: string, selected: boolean) => void;
  onToggleDivision: (division: string, selected: boolean) => void;
  onItemsSelection: (items: ComparisonItem[]) => void;
  onItemPurposeFlagUpdate: (itemId: string, flags: string[]) => void;
  onGifItemsSelection: (items: BMOGIFItem[]) => void;
  onToggleGifBaseScenario: (value: boolean) => void;
  riskProfile?: RiskProfileItem;
  investorQuestionnaire?: KycQuestionnaire | ProposalOmniRiskProfilesQuestionnaire;
  comparisonToolbarOptions?: ComparisonToolbarOptions;
  shortHistory?: boolean;
  proposalContact?: ProposalNS.ProposalContact;
  proposalDate?: Date;
  proposalTemplateOptions: ProposalTemplateOptions;
  setToolbarOption: (key: string, toolbarOption: ProposalToolbarOption) => void;
  toolbarOptions: Dictionary<ProposalToolbarOption>;
  onChangeTemplate: () => void;
  onLocaleChange: (locale: Locale) => void;
  onProposalStyleOptionsChange: (styleOptions: ProposalNS.ProposalStyleOptionsV2) => void;
  setTextAreaData: (key: string, textAreaData: ProposalTextAreaData) => void;
  setSelectedView: (templateKey: string, key: string, value: string) => void;
  gifHideBaseScenario?: boolean;
  onToggleShortHistory: (value: boolean) => void;
  advisorContext?: Nullable<AdvisorContext>;
  rationaleAnswers?: QuestionnaireAnswers;
  onEditBrandingInfo: () => void;
  onChangeItemOrder: (itemId: string, referenceId: string, position?: DropItemPosition) => void;
  onChangeContentOrder: (itemId: string, referenceId: string, position?: DropItemPosition) => void;
  downloadPdf: () => void;
  pageRefs: Array<React.RefObject<HTMLDivElement>>;
};

type State = {
  styleSheetLoaded: boolean;
  calculatedHistoricalScenarios?: CalculatedHistoricalScenario[];
};

class ProposalBuilderComponent extends React.Component<Props, State> {
  pageIndexes: ProposalNS.ProposalSectionPageMapping;

  constructor(props: Props) {
    super(props);
    this.pageIndexes = {};
    registerDAOs();
    this.state = {
      styleSheetLoaded: false,
      calculatedHistoricalScenarios: [],
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.template !== this.props.template && this.state.styleSheetLoaded) {
      this.styleSheetReloading();
    }

    if (
      prevProps.comparison.selectedHistoricalScenarios !== undefined &&
      this.props.comparison.selectedHistoricalScenarios !== undefined &&
      (!isEqual(prevProps.comparison.selectedHistoricalScenarios, this.props.comparison.selectedHistoricalScenarios) ||
        (this.state.calculatedHistoricalScenarios?.length === 0 && this.props.comparison.selectedHistoricalScenarios?.length !== 0))
    ) {
      this.calculateHistoricalScenarios(this.props.comparison.selectedHistoricalScenarios || []);
    }
  }

  calculateHistoricalScenarios = async (scenarios: HistoricalScenario[]) => {
    if (!scenarios || !scenarios.length) {
      this.setState({ calculatedHistoricalScenarios: [] });
      return;
    }
    const historicalScenarios = await this.props.calculateHistoricalScenarios(scenarios, false);
    this.setState({ calculatedHistoricalScenarios: historicalScenarios });
  };

  setPageOverflowIndex(elements: ProposalNS.ProposalTemplateElement[], index: number, proposal?: ProposalNS.Proposal): void {
    for (const element of elements) {
      if (element.type === ProposalNS.ProposalTemplateElementType.Data) {
        const dataElement = element as ProposalElementsNS.ProposalDataElement;
        dataElement.pageOverflowIndex = index;

        // Rebuild DAO for multi-page sections to ensure correct pageOverflowIndex
        if (dataElement.daoObject && index > 0 && proposal) {
          dataElement.daoObject = DAOManager.getInstance().buildDAO(dataElement.DAO.type, proposal, dataElement);
        }
      } else if (element.type === ProposalNS.ProposalTemplateElementType.Layout) {
        this.setPageOverflowIndex(element.elements, index, proposal);
      }
    }
  }

  splitProposalInPages(
    sectionElementMap: Array<ProposalElementsNS.ProposalSectionElement>,
    proposal: ProposalNS.Proposal,
  ): ProposalElementsNS.ProposalSectionElement[][] {
    const sectionElements: ProposalElementsNS.ProposalSectionElement[] = sectionElementMap;
    const { orientation } = this.props.proposalTemplateOptions.styleOptions;
    let i = 0;
    const proposalPages = sectionElements.slice().reduce((pages, sectionElement) => {
      const newPages = pages;
      const { hiddenLayouts } = this.props.proposalTemplateOptions;

      // Use the template-assigned page fraction (no dynamic calculation)
      const sectionElementPageFraction = ProposalTemplateUtils.getSectionElementPageFraction(sectionElement, orientation, hiddenLayouts);

      // Check if this section contains Asset allocation data that might overflow
      const hasAssetAllocation = hasAssetAllocationData(sectionElement);
      const hasAssetAllocationDonut = hasAssetAllocationDonutData(sectionElement);
      let additionalPages = 0;
      let adjustedSectionElementPageFraction = sectionElementPageFraction;

      if (hasAssetAllocation) {
        if (hasAssetAllocationDonut) {
          // For donut views, check if content overflows the assigned page fraction
          const donutData = getAssetAllocationDonutData(sectionElement);
          if (donutData && donutData.data && donutData.data[0]) {
            const totalDataPoints = donutData.data[0].length;
            additionalPages = calculateAssetAllocationDonutAdditionalPages(totalDataPoints, sectionElementPageFraction, orientation);
          }
        } else {
          // For table views, use the exact same logic as AssetAllocationDAO
          const totalRowCount = getAssetAllocationRowCount(sectionElement);
          if (totalRowCount !== null) {
            // Use utility function to calculate additional pages needed
            additionalPages = calculateAssetAllocationAdditionalPages(totalRowCount, orientation);

            // If Asset Allocation will overflow to additional pages, force it to use full page fraction
            // This prevents content being cut off when sharing space with other sections
            // TODO: Refactor code at global level to adjust rows in remaining fraction accordingly
            if (additionalPages > 0) {
              adjustedSectionElementPageFraction = 1.0;
            }
          }
        }
      }

      // If there's already a page being built up, see if there's enough space for the next
      // module (including custom disclaimers if applicable).
      if (newPages[i]) {
        const remainingFraction = ProposalTemplateUtils.calculateRemainingProposalPageFraction(newPages[i], orientation, hiddenLayouts);

        // All sections should use their assigned page fraction to determine if they can fit
        // This ensures consistent behavior and respects the template designer's intentions
        // Use the adjusted page fraction for Asset Allocation sections that will overflow
        const shouldMoveToNewPage = remainingFraction.toFixed(4) < adjustedSectionElementPageFraction.toFixed(4);

        if (shouldMoveToNewPage) {
          i++;
        }
      }

      // Handle sections that need multiple pages (either from template pageFraction > 1 or content overflow)
      const totalPagesNeeded = Math.max(Math.ceil(adjustedSectionElementPageFraction), 1 + additionalPages);

      if (totalPagesNeeded > 1) {
        let localIndex = 0;

        if (adjustedSectionElementPageFraction > 1) {
          // Original logic for template-defined multi-page sections
          let fraction = adjustedSectionElementPageFraction;
          while (fraction > 0) {
            const newSectionElement: ProposalElementsNS.ProposalSectionElement = deepClone(sectionElement);
            newSectionElement.pageOverflowIndex = localIndex;

            if (adjustedSectionElementPageFraction !== sectionElementPageFraction) {
              newSectionElement.pageFraction = {
                ...newSectionElement.pageFraction,
                [orientation]: adjustedSectionElementPageFraction,
              };
            }

            this.setPageOverflowIndex(newSectionElement.layout.elements, localIndex++, proposal);
            newPages[i] = [newSectionElement];

            fraction -= 1;
            i++;
          }
        } else {
          // New logic for content overflow (Asset allocation tables)
          for (let pageIndex = 0; pageIndex < totalPagesNeeded; pageIndex++) {
            const newSectionElement: ProposalElementsNS.ProposalSectionElement = deepClone(sectionElement);
            newSectionElement.pageOverflowIndex = pageIndex;

            // Apply adjusted page fraction for Asset Allocation sections that overflow
            if (adjustedSectionElementPageFraction !== sectionElementPageFraction) {
              newSectionElement.pageFraction = {
                ...newSectionElement.pageFraction,
                [orientation]: adjustedSectionElementPageFraction,
              };
            }

            this.setPageOverflowIndex(newSectionElement.layout.elements, pageIndex, proposal);

            if (pageIndex === 0) {
              // First page can share space with other sections if there's room
              newPages[i] = (newPages[i] || []).concat(newSectionElement);
            } else {
              // Additional overflow pages start with just the overflow section
              // but can have other sections appended to them later if there's space
              newPages[i + pageIndex] = [newSectionElement];
            }
          }
          i += additionalPages;
        }
      } else {
        // Single page section
        let finalSectionElement = sectionElement;

        if (adjustedSectionElementPageFraction !== sectionElementPageFraction) {
          finalSectionElement = deepClone(sectionElement);
          finalSectionElement.pageFraction = {
            ...finalSectionElement.pageFraction,
            [orientation]: adjustedSectionElementPageFraction,
          };
        }

        newPages[i] = (newPages[i] || []).concat(finalSectionElement);
      }

      return newPages;
    }, [] as ProposalElementsNS.ProposalSectionElement[][]);

    return proposalPages;
  }

  preprocessTemplate = (selectedSections: string[]): ProposalNS.ProposalTemplateV2 => {
    const {
      template,
      proposalItems,
      proposalTemplateOptions,
      proposalGifItems,
      gifHideBaseScenario,
      riskProfile,
      rationaleAnswers,
      investorQuestionnaire,
      comparison,
      user,
    } = this.props;

    /*
    The code called here is custom to some sections. We should try to avoid doing this as much as we can.
    Should you get inspiration here, please ask yourself: should I try to generalize what is shared? Would the benefit exceed the cost?
    */
    let processedTemplate = template;

    processedTemplate = preprocessCorrelationMatrices(selectedSections, processedTemplate, proposalTemplateOptions, proposalItems);
    processedTemplate = preprocessBMOGif(selectedSections, proposalGifItems, processedTemplate, gifHideBaseScenario === true, proposalTemplateOptions);
    processedTemplate = preprocessIncomeStream(selectedSections, processedTemplate, proposalItems, proposalTemplateOptions, comparison.showBaseCurrencies);
    processedTemplate = preprocessRiskProfiles(selectedSections, processedTemplate, riskProfile, proposalItems, proposalTemplateOptions, user);
    processedTemplate = preprocessSuitabilityDetermination(selectedSections, processedTemplate, rationaleAnswers, proposalTemplateOptions);
    processedTemplate = preprocessInvestorQuestionnaire(selectedSections, processedTemplate, proposalTemplateOptions, investorQuestionnaire, riskProfile);
    processedTemplate = preprocessInstrumentCustomDisclaimers(processedTemplate, proposalTemplateOptions, proposalItems);
    processedTemplate = preprocessBenchmarksBreakdownDisclaimers(processedTemplate, proposalTemplateOptions, proposalItems);
    processedTemplate = preprocessRebalanceAnalysis(selectedSections, processedTemplate, proposalItems, proposalTemplateOptions);

    return processedTemplate;
  };

  gatherCustomStrings = (): Dictionary<LocalizedString | string> => {
    const { advisorContext } = this.props;

    const customStrings: Dictionary<LocalizedString | string> = {};

    if (advisorContext?.advisor) {
      const { advisor } = advisorContext;

      if (advisor.dealerName?.short) {
        customStrings.dealerName = advisor.dealerName.short;
      }

      if (advisor.firstName) {
        customStrings.advisorFirstName = advisor.firstName;
      }

      if (advisor.lastName) {
        customStrings.advisorLastName = advisor.lastName;
      }
    }

    return customStrings;
  };

  getSelectedDivision = (divisions: ProposalNS.ProposalTemplateDivisionElement[], selected: string) =>
    divisions.find(division =>
      division.sections.some(section => {
        const isCustomRiskProfile =
          selected === ProposalNS.ProposalSectionType.CustomRiskProfile && section.key?.indexOf(ProposalNS.ProposalSectionType.CustomRiskProfile) !== -1;
        const isQuestionnaire = section.key?.indexOf(selected) !== -1 && section.sectionType === ProposalNS.ProposalSectionType.Questionnaire;
        return section.key === selected || isCustomRiskProfile || isQuestionnaire;
      }),
    );

  getSelectedAndForcedSections = (division: ProposalNS.ProposalTemplateDivisionElement, selected: string) => {
    const selectedSections = division.sections.filter(
      section =>
        (!!section.forced &&
          (section.sidebarControl === ProposalNS.ProposalSidebarControl.Hide || section.sidebarControl === ProposalNS.ProposalSidebarControl.None)) ||
        section.key === selected,
    );

    if (selectedSections.length) {
      return {
        ...division,
        sections: [...selectedSections],
      };
    }

    return division;
  };

  getDivisionAndSelectedSections = (division: ProposalNS.ProposalTemplateDivisionElement, selected: string) => {
    const selectedSections = division.sections.filter(section => {
      const isCustomRiskProfile =
        selected === ProposalNS.ProposalSectionType.CustomRiskProfile && section.key?.indexOf(ProposalNS.ProposalSectionType.CustomRiskProfile) !== -1;
      const isQuestionnaire = section.key?.indexOf(selected) !== -1 && section.sectionType === ProposalNS.ProposalSectionType.Questionnaire;
      return section.key === selected || isCustomRiskProfile || isQuestionnaire;
    });

    if (selectedSections.length) {
      return {
        ...division,
        sections: [...selectedSections],
      };
    }

    return {
      ...division,
      sections: [],
    };
  };

  // Return an array of divisions with only the selected sections
  getOrderedDivisions = (rootDivisions: ProposalNS.ProposalTemplateDivisionElement[], sections: string[]) =>
    sections.reduce((divisions: ProposalNS.ProposalTemplateDivisionElement[], division: string) => {
      const selectedDivision = this.getSelectedDivision(rootDivisions, division);

      if (selectedDivision) {
        const divisionAndSelectedSection = this.getDivisionAndSelectedSections(selectedDivision, division);
        divisions = [...divisions, divisionAndSelectedSection];
      }

      return divisions;
    }, []);

  /**
   * This function is used to determine the order of Grouped Sections.
   * @param {ProposalNS.Proposal} rootDivisions proposal.processedTemplate.root ->
   * @param {string[]} sections list of all sections keys, provided by this.props.proposalTemplateOptions
   * @return Sections and Divisions sorted
   */
  setGroupedSectionsOrder = (rootDivisions: ProposalNS.ProposalTemplateDivisionElement[], sections?: string[]) => {
    const { user } = this.props;
    if (!sections || !sections.length) return undefined;

    const rootSections = deepClone(rootDivisions.map(division => division.sections).flat(1));
    const coverSections = filterSectionsByType(rootSections, ProposalNS.ProposalSectionType.Cover);
    const instrumentCustomDisclaimersSections = filterSectionsByType(rootSections, ProposalNS.ProposalSectionType.InstrumentCustomDisclaimers);
    const disclaimerSections = filterSectionsByType(rootSections, ProposalNS.ProposalSectionType.Disclaimer);
    const staticTopSections = rootSections.filter(section => section && section.key && coverSections.indexOf(section.key) !== -1 && !!section.forced);

    const staticBottomSections = rootSections.filter(
      section =>
        section &&
        section.key &&
        (section.key.includes(ProposalNS.ProposalSectionType.BmoGifSummary) ||
          section.key.includes(ProposalNS.DAOType.singlePageFund) ||
          section.key.includes(ProposalNS.DAOType.singlePagePortfolio) ||
          section.key.includes(ProposalNS.DAOType.singlePageGroup)),
    );

    const staticDisclaimersSections = rootSections.filter(
      section =>
        section && section.key && (section.sectionType === ProposalNS.ProposalSectionType.Disclaimer || disclaimerSections.indexOf(section.key) !== -1),
    );

    const customDisclaimerSections = rootSections.filter(
      section =>
        section &&
        section.key &&
        (section.sectionType === ProposalNS.ProposalSectionType.InstrumentCustomDisclaimers || instrumentCustomDisclaimersSections.indexOf(section.key) !== -1),
    );

    let sectionsCopy: string[] = deepClone(sections);

    if (sections.indexOf(ProposalNS.ProposalSectionType.BmoGifSummary) !== -1) {
      sectionsCopy = sections.filter(section => section !== ProposalNS.ProposalSectionType.BmoGifSummary);
    }

    let orderedSections = this.getOrderedDivisions(rootDivisions, sectionsCopy)
      .map(division => division.sections)
      .flat(1);

    if (staticTopSections.length) {
      orderedSections = [...staticTopSections, ...orderedSections];
    }

    if (userHasPermissionEnabled(user, PERMISSIONS.FORCED_PAGE_ORDER)) {
      const sortedMarketingSections = sortSectionsBySectionKey(sections, rootSections, ProposalNS.ProposalSectionType.MarketingPageSort);
      // NOTE: staticTopSections excludes pages which are of sectionType === COVER, that are not "forced"
      // We need to get all pages which should remain at the top to be appended at the beginning of the orderedSections array
      const cover = rootSections.filter(section => section && section.key && coverSections.indexOf(section.key) !== -1);
      const allCoverPages = Object.assign(staticTopSections, cover);

      const marketingPageSections = sections.filter(sectionKey => sectionKey.includes(ProposalNS.ProposalSectionType.MarketingPageSort));

      const filteredSections = orderedSections.filter(section => !coverSections.includes(section.key) && !marketingPageSections.includes(section.key));

      orderedSections = [...allCoverPages, ...sortedMarketingSections, ...filteredSections];
    }

    if (staticBottomSections.length) {
      orderedSections = [...orderedSections, ...staticBottomSections];
    }

    if (customDisclaimerSections.length) {
      const sectionsKeys = customDisclaimerSections.map(section => section.key);
      const filteredSections = orderedSections.filter(section => !sectionsKeys.includes(section.key));
      orderedSections = [...filteredSections, ...customDisclaimerSections];
    }

    if (staticDisclaimersSections.length) {
      const sectionsKeys = staticDisclaimersSections.map(section => section.key);
      const filteredSections = orderedSections.filter(section => !sectionsKeys.includes(section.key));
      orderedSections = [...filteredSections, ...staticDisclaimersSections];
    }

    return orderedSections;
  };

  /**
   * This function is used to group root items by their sections. "root" provided by state.proposalV2.selectedTemplate.root
   * @param {string[]} sections list of all sections keys, provided by this.props.proposalTemplateOptions
   * @return sections grouped by type and maintains the order of pages every re-render
   */
  groupProposalSectionsByType = (sections: string[]) => {
    const {
      template: { root },
      user,
    } = this.props;
    const rootSections = root.map(division => division.sections).flat(1);
    const bmoGifSections = filterSectionsByType(rootSections, ProposalNS.ProposalSectionType.BmoGifSummary);
    const coverSections = filterSectionsByType(rootSections, ProposalNS.ProposalSectionType.Cover);
    const customTopSections = filterSectionsByType(rootSections, ProposalNS.ProposalSectionType.CustomTop);
    const disclaimerSections = filterSectionsByType(rootSections, ProposalNS.ProposalSectionType.Disclaimer);
    const marketingSections = filterSectionsByType(rootSections, ProposalNS.ProposalSectionType.Marketing);
    const questionnaireSections = filterSectionsByType(rootSections, ProposalNS.ProposalSectionType.Questionnaire);

    if (userHasPermissionEnabled(user, PERMISSIONS.FORCED_PAGE_ORDER)) {
      return sections.sort((a, b) => {
        const setSectionOrder = (key: string) => {
          if (coverSections.includes(key)) return 1;
          if (customTopSections.includes(key)) return 2;
          if (marketingSections.includes(key)) return 3;
          if (bmoGifSections.includes(key)) return 4;
          if (questionnaireSections.includes(key) && !!rootSections.find(section => section.key === key)?.forced) return 5;
          if (disclaimerSections.includes(key)) return 6;
          return 7;
        };

        const aOrder = setSectionOrder(a);
        const bOrder = setSectionOrder(b);

        if (aOrder !== bOrder) return aOrder - bOrder;

        return 0;
      });
    }
    // NOTE: The previous implementation of this function (below) resulted in the pages within a section to be sorted backward and forward on every re-render
    // In order to move toward a more robust solution while reducing risk, the above sort function was developed and placed behind a feature flag
    // Going forward, we will ideally have every client use the above sort function until the legacy one can be removed completely.
    return sections.sort((a, b) => {
      const aSection = rootSections.find(section => section.key === a);
      const bSection = rootSections.find(section => section.key === b);
      if (coverSections.indexOf(a) !== -1) return -1;
      if (coverSections.indexOf(b) !== -1) return 1;
      if (customTopSections.indexOf(a) !== -1) return -1;
      if (customTopSections.indexOf(b) !== -1) return 1;
      if (bmoGifSections.indexOf(a) !== -1) return 1;
      if (bmoGifSections.indexOf(b) !== -1) return -1;
      if (!!aSection?.forced && questionnaireSections.indexOf(a) !== -1) return 1;
      if (!!bSection?.forced && questionnaireSections.indexOf(b) !== -1) return -1;
      if (disclaimerSections.indexOf(a) !== -1) return 1;
      if (disclaimerSections.indexOf(b) !== -1) return -1;
      return 0;
    });
  };

  handleJumpToPage = (section: string, scrollOptions: ScrollIntoViewOptions = {}) => {
    let pageStart = this.pageIndexes && this.pageIndexes[`${section}`] ? this.pageIndexes[`${section}`].start : 0;

    if (this.pageIndexes) {
      for (const index in this.pageIndexes) {
        if (index.indexOf(section) !== -1) {
          pageStart = this.pageIndexes ? this.pageIndexes[index]?.start : 0;
          break;
        }
      }
    }

    this.props.pageRefs.find(ref => ref.current?.classList.contains(`page-${pageStart}`))?.current?.scrollIntoView({ behavior: 'smooth', ...scrollOptions });
  };

  handleOnToggleHiddenLayout = (hiddenLayout: string, selected: boolean): void => {
    this.props.onToggleHiddenLayout(hiddenLayout, selected);
  };

  handleOnToggleSection = (section: string, selected: boolean): void => {
    this.props.onToggleSection(section, selected);

    if (selected) {
      setTimeout(() => this.handleJumpToPage(section, { block: 'nearest', inline: 'nearest' }), 0);
    }
  };

  styleSheetLoaded = () => {
    this.setState({ styleSheetLoaded: true });
  };

  styleSheetReloading = () => {
    this.setState({ styleSheetLoaded: false });
  };

  render() {
    const {
      user,
      proposalItems,
      proposalItemsCorrelation,
      proposalGifItems,
      selectedSidebarTab,
      template,
      riskProfile,
      toolbarOptions,
      comparison,
      shortHistory,
      templates,
      rationaleAnswers,
      proposalContact,
      proposalDate,
      proposalUser,
      comparisonToolbarOptions,
      proposalTemplateOptions,
      proposalTemplateOptions: { divisions, comments, views },
      gifHideBaseScenario,
      locale,
    } = this.props;

    const rootSections = template.root.map(division => division.sections).flat(1);
    const questionnaires = filterSectionsByType(rootSections, ProposalNS.ProposalSectionType.Questionnaire);

    let { sections } = this.props.proposalTemplateOptions;

    // If proposal is GIF only, keep selection empty to only include GIF illustration.
    if (proposalItems.length === 0) {
      sections = [];
    }

    if (proposalGifItems.length > 0 && sections.indexOf(ProposalNS.ProposalSectionType.BmoGifSummary) === -1) {
      sections.push(ProposalNS.ProposalSectionType.BmoGifSummary);
    }

    // If proposal has questionnaire sections forced and hidden, the questionnaire sections should be in the sections list
    if (questionnaires.length) {
      questionnaires.forEach(questionnaire => {
        if (sections.indexOf(questionnaire) === -1) {
          const section = rootSections.find(section => section.key === questionnaire && section.sectionType === ProposalNS.ProposalSectionType.Questionnaire);
          const isForced = !!section?.forced;
          const isHidden =
            section?.sidebarControl === ProposalNS.ProposalSidebarControl.Hide || section?.sidebarControl === ProposalNS.ProposalSidebarControl.None;
          if (isForced && isHidden) {
            sections.push(questionnaire);
          }
        }
      });
    }

    if (userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_TOOLBOX_NEW_MENU)) {
      sections = this.groupProposalSectionsByType(sections);
    }

    const processedTemplate: ProposalNS.ProposalTemplateV2 = this.preprocessTemplate(sections);

    const comparisonName = processedTemplate.customHeader !== undefined && processedTemplate.customHeader.showComparisonName ? comparison.name : undefined;

    const proposalLocale = comparison.proposalLocale != null ? comparison.proposalLocale : proposalTemplateOptions.locale;

    const proposal: ProposalNS.Proposal = {
      user: proposalUser,
      appUser: user,
      comparisonName,
      readOnly: comparisonIsReadOnly(comparison),
      items: proposalItems,
      itemsCorrelation: proposalItemsCorrelation,
      gifItems: proposalGifItems,
      template: processedTemplate,
      riskProfile,
      toolbarOptions,
      rationaleAnswers,
      options: {
        theme: proposalTemplateOptions.theme,
        locale: proposalLocale,
        comments,
        includeFundProfiles: proposalTemplateOptions.styleOptions.includeFundProfiles === true,
        includePortfolioProfiles: proposalTemplateOptions.styleOptions.includePortfolioProfiles === true,
        includeGroupProfiles: proposalTemplateOptions.styleOptions.includeGroupProfiles === true,
        orientation: proposalTemplateOptions.styleOptions.orientation,
      },
      shortHistory,
      contact: proposalContact,
      date: proposalDate,
      comparisonToolbarOptions,
      showBaseCurrencies: comparison.showBaseCurrencies,
      comment: comparison.comment,
      itemIterators: new Map<string, number>(),
      hideBaseGifScenarios: gifHideBaseScenario,
      selectedBenchmarkId: comparison.selectedBenchmarkId || undefined,
      customStrings: this.gatherCustomStrings(),
      tableOfContents: [],
      selectedHistoricalScenarios: comparison.selectedHistoricalScenarios,
      calculatedHistoricalScenarios: this.state.calculatedHistoricalScenarios,
      commonInception: getCommonInception({ items: proposalItems }, [ComparisonItemsFilter.All], shortHistory),
      monthEndDataOption: comparison.monthEndDataOption,
      taxAnalysis: comparison.taxAnalysis,
      accountSets: remapAccountSetIndices(comparison.accountSets, comparison.items, proposalItems),
    };

    // Remove disabled sections. Prevents checked but disabled sections from automatically re-enabling when the disable rules are cleared
    proposalTemplateOptions.sections = proposalTemplateOptions.sections.filter(key => {
      const section = proposal.template.root.flatMap(division => division.sections).find(section => new RegExp(`\\b${key}\\b`).test(section.key));

      if (!section) {
        return false;
      }

      return section.disableRules == null || validateDisableRules(section.disableRules, sections, proposal.items, proposal.riskProfile).isDisabled === false;
    });

    const sideBarProposal: ProposalNS.Proposal = { ...proposal };
    sideBarProposal.template = template;

    const sidebarToDisplay = selectedSidebarTab !== undefined && template.sidebarTabs.find(tab => tab.key === selectedSidebarTab);
    const customSectionsOrder =
      userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_TOOLBOX_NEW_MENU) && proposalTemplateOptions.sections.length
        ? this.setGroupedSectionsOrder(proposal.template.root, sections)
        : undefined;

    const sectionElements: Array<ProposalElementsNS.ProposalSectionElement> = buildProposalSectionElements(
      proposal,
      sections,
      views !== undefined ? views : {},
      user,
      customSectionsOrder,
    );

    const pages = this.splitProposalInPages(sectionElements, proposal);
    this.pageIndexes = ProposalTemplateUtils.generateSectionPageIndexes(pages);
    ProposalTemplateUtils.mergeAndSinkDisclaimers(pages);

    proposal.tableOfContents = pages
      .reduce((toc, curr, pageIndex) => {
        // If the section does not have a tableOfContentsGroup we skip it
        if (!curr[0].tableOfContentsGroup) {
          return toc;
        }

        const i = toc.findIndex(x => x.tableOfContentsGroup.key === curr[0].tableOfContentsGroup?.key);
        // eslint-disable-next-line no-sequences
        return (i === -1 ? toc.push({ tableOfContentsGroup: curr[0].tableOfContentsGroup, startsAtPageIndex: pageIndex, pages: 1 }) : toc[i].pages++, toc);
      }, proposal.tableOfContents)
      .filter(item => item.tableOfContentsGroup?.key !== undefined && item.tableOfContentsGroup.key !== ProposalNS.DAOType.TableOfContents);

    // Desktop and mobile toolbox actions
    const onPaperFormatChange = (paperFormat: ProposalPaperFormat) => {
      const { styleOptions } = proposalTemplateOptions;

      if (paperFormat !== styleOptions.paperFormat) {
        this.props.onProposalStyleOptionsChange({
          ...styleOptions,
          paperFormat,
        });
      }
    };

    const onToggleFundProfiles = (value: boolean) => {
      const { styleOptions } = proposalTemplateOptions;

      if (styleOptions.includeFundProfiles !== value) {
        const newOptions: ProposalNS.ProposalStyleOptionsV2 = {
          ...styleOptions,
          includeFundProfiles: value,
        };

        this.props.onProposalStyleOptionsChange(newOptions);
      }
    };

    const onTogglePortfolioProfiles = (value: boolean) => {
      const { styleOptions } = proposalTemplateOptions;

      if (styleOptions.includePortfolioProfiles !== value) {
        const newOptions: ProposalNS.ProposalStyleOptionsV2 = {
          ...styleOptions,
          includePortfolioProfiles: value,
        };

        this.props.onProposalStyleOptionsChange(newOptions);
      }
    };

    const onToggleGroupProfiles = (value: boolean) => {
      const { styleOptions } = proposalTemplateOptions;

      if (styleOptions.includeGroupProfiles !== value) {
        const newOptions: ProposalNS.ProposalStyleOptionsV2 = {
          ...styleOptions,
          includeGroupProfiles: value,
        };

        this.props.onProposalStyleOptionsChange(newOptions);
      }
    };

    const onOrientationChange = (orientation: ProposalOrientation) => {
      const { styleOptions } = proposalTemplateOptions;
      const { items, template } = proposal;
      const maxProposalItems = getMaxNumberOfItems(template, orientation);

      if (items.length > maxProposalItems) {
        const data = items.slice(0, maxProposalItems);
        this.props.onItemsSelection(data);
      }

      const newOptions: ProposalNS.ProposalStyleOptionsV2 = {
        ...styleOptions,
        orientation,
      };

      this.props.onProposalStyleOptionsChange(newOptions);
    };

    const onHeaderPatternChange = (pattern: string) => {
      const { styleOptions } = proposalTemplateOptions;

      const newOptions: ProposalNS.ProposalStyleOptionsV2 = {
        ...styleOptions,
        banners: {
          ...styleOptions.banners,
          pattern,
        },
      };

      this.props.onProposalStyleOptionsChange(newOptions);
    };

    const onHeaderColorChange = (headerColor: string) => {
      const { styleOptions } = proposalTemplateOptions;

      const style = PROPOSAL_BANNER_STYLES.find(s => s.background === headerColor);

      if (!style) {
        return;
      }

      const newOptions: ProposalNS.ProposalStyleOptionsV2 = {
        ...styleOptions,
        banners: {
          ...styleOptions.banners,
          ...style,
        },
      };

      this.props.onProposalStyleOptionsChange(newOptions);
    };

    const onCoBrandingColorChange = (selectedColor: string, key: string) => {
      const { styleOptions } = proposalTemplateOptions;

      const style = {
        coBrandingColors: {
          [key]: selectedColor,
        },
      };

      const newOptions: ProposalNS.ProposalStyleOptionsV2 = {
        ...styleOptions,
        banners: {
          ...styleOptions.banners,
          ...style,
        },
      };

      this.props.onProposalStyleOptionsChange(newOptions);
    };

    const actions: ProposalActions = {
      onToggleHiddenLayout: this.props.onToggleHiddenLayout,
      onToggleSection: this.handleOnToggleSection,
      onToggleDivision: this.props.onToggleDivision,
      onToggleFundProfiles,
      onToggleGroupProfiles,
      onTogglePortfolioProfiles,
      onToggleShortHistory: this.props.onToggleShortHistory,
      onToggleGifBaseScenario: this.props.onToggleGifBaseScenario,
      onItemsSelection: this.props.onItemsSelection,
      onItemPurposeFlagUpdate: this.props.onItemPurposeFlagUpdate,
      onGifItemsSelection: this.props.onGifItemsSelection,
      onHeaderPatternChange,
      onHeaderColorChange,
      onCoBrandingColorChange,
      onOrientationChange,
      onPaperFormatChange,
      onLocaleChange: this.props.onLocaleChange,
      onProposalStyleOptionsChange: this.props.onProposalStyleOptionsChange,
      onChangeTemplate: this.props.onChangeTemplate,
      onEditBrandingInfo: this.props.onEditBrandingInfo,
      onChangeItemOrder: this.props.onChangeItemOrder,
      onChangeContentOrder: this.props.onChangeContentOrder,
      downloadPdf: this.props.downloadPdf,
      onJumpToPage: this.handleJumpToPage,
    };
    // TODO - remove v2 in class name when we fully switch to the new builder. Added so we don't inherit styles from the old builder
    return (
      <ProposalActionsProvider actions={actions}>
        <div className="proposal-builder-v2 d-flex flex-grow-1">
          <div className="d-flex proposal-builder-content">
            {sidebarToDisplay && userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_TOOLBOX_NEW_MENU) && (
              <div className={classNames({ 'desktop-toolbox': userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_ACCESSIBILITY) })}>
                <ProposalToolbox
                  user={user}
                  proposal={sideBarProposal}
                  proposalTemplateOptions={proposalTemplateOptions}
                  onJumpToPage={this.handleJumpToPage}
                  onToggleHiddenLayout={this.props.onToggleHiddenLayout}
                  onToggleSection={this.handleOnToggleSection}
                  onToggleDivision={this.props.onToggleDivision}
                  locale={locale}
                  comparison={comparison}
                  onItemsSelection={this.props.onItemsSelection}
                  onItemPurposeFlagUpdate={this.props.onItemPurposeFlagUpdate}
                  onGifItemsSelection={this.props.onGifItemsSelection}
                  onToggleGifBaseScenario={this.props.onToggleGifBaseScenario}
                  onChangeTemplate={this.props.onChangeTemplate}
                  onEditBrandingInfo={this.props.onEditBrandingInfo}
                  onProposalStyleOptionsChange={this.props.onProposalStyleOptionsChange}
                  templates={templates}
                  gifHideBaseScenario={gifHideBaseScenario}
                  onToggleShortHistory={this.props.onToggleShortHistory}
                  onChangeItemOrder={this.props.onChangeItemOrder}
                  onChangeContentOrder={this.props.onChangeContentOrder}
                  template={template}
                  downloadPdf={this.props.downloadPdf}
                />
              </div>
            )}
            {sidebarToDisplay && !userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_TOOLBOX_NEW_MENU) && (
              <ProposalSidebar
                user={user}
                locale={locale}
                proposal={sideBarProposal}
                comparison={comparison}
                selectedTab={sidebarToDisplay}
                proposalTemplateOptions={proposalTemplateOptions}
                onToggleSection={this.handleOnToggleSection}
                onToggleDivision={this.props.onToggleDivision}
                onItemsSelection={this.props.onItemsSelection}
                onItemPurposeFlagUpdate={this.props.onItemPurposeFlagUpdate}
                onGifItemsSelection={this.props.onGifItemsSelection}
                onToggleGifBaseScenario={this.props.onToggleGifBaseScenario}
                onChangeTemplate={this.props.onChangeTemplate}
                onLocaleChange={this.props.onLocaleChange}
                onEditBrandingInfo={this.props.onEditBrandingInfo}
                onProposalStyleOptionsChange={this.props.onProposalStyleOptionsChange}
                templates={templates}
                gifHideBaseScenario={gifHideBaseScenario}
                onToggleShortHistory={this.props.onToggleShortHistory}
                onChangeItemOrder={this.props.onChangeItemOrder}
                onJumpToPage={this.handleJumpToPage}
                pageIndexes={this.pageIndexes}
              />
            )}

            <div className="proposal-preview-content">
              <div className="proposal-scroll-container">
                <ProposalPreviewComponent
                  user={user}
                  proposal={proposal}
                  selectedDivisions={divisions}
                  selectedSections={sections}
                  selectedViews={views !== undefined ? views : {}}
                  setToolbarOption={this.props.setToolbarOption}
                  proposalTemplateOptions={proposalTemplateOptions}
                  setTextAreaData={this.props.setTextAreaData}
                  setSelectedView={(key, value) => this.props.setSelectedView(template.key, key, value)}
                  pages={pages}
                  pageRefs={this.props.pageRefs}
                  styleSheetLoaded={this.state.styleSheetLoaded}
                />
              </div>
            </div>
          </div>
        </div>

        {/*
          Having the proposal style loader this deep in the app isn't ideal, but this allows us to benefit from the
          refresh strategy put in place to refresh both the template and the styles with a simple action.
        */}
        <ProposalStyleLoader onLoad={this.styleSheetLoaded} onError={this.styleSheetLoaded} />
        {userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_ACCESSIBILITY) && userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_TOOLBOX_NEW_MENU) && (
          <div className="mobile-toolbox">
            <BottomToolbox
              template={template}
              comparison={comparison}
              proposal={sideBarProposal}
              proposalTemplateOptions={proposalTemplateOptions}
              user={user}
              locale={locale}
              downloadPdf={this.props.downloadPdf}
            />
          </div>
        )}
      </ProposalActionsProvider>
    );
  }
}

export default memo(ProposalBuilderComponent, isEqual);
